/* eslint-disable */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  STORAGE,
  loadTrash,
  saveTrash,
  removeFromTrash,
  popFromTrash,
  emptyTrash,
  daysLeftInTrash,
  loadDecoys,
  saveDecoys,
  t,
  getLocale,
} from "@/lib/mr";

/**
 * "Recently Deleted" folder — mirrors iOS Notes' trash view.
 *   • Rows show title, date deleted (using the standard iOS grouping),
 *     preview snippet, and an "N days left" badge.
 *   • Swipe left on a row → reveals a [Recover] and a red [Delete]
 *     button; tapping [Recover] pops the note back to /notes, [Delete]
 *     purges it permanently.
 *   • Top-right "Edit" collapses into an "Empty" affordance we use to
 *     trigger the "Delete All" confirmation dialog (parity with iOS).
 *
 * Persistence: `loadTrash` auto-purges anything past the 30-day
 * lifetime on every read, so no cron-like housekeeping is needed.
 */
export default function RecentlyDeleted() {
  const navigate = useNavigate();
  const langCode = localStorage.getItem(STORAGE.LANGUAGE) || "auto";
  const locale = getLocale(langCode);
  const isRTL = /^(?:ar|he|fa|ur)/.test(langCode || "");

  const [trash, setTrash] = useState(() => loadTrash());
  const [swipedRowId, setSwipedRowId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Track live row transforms so a partial swipe stays open until we
  // release it explicitly (matches iOS behaviour). Kept in a ref
  // because we mutate imperatively during pointer drag frames — going
  // through setState here would blow the 60 fps budget.
  const rowRefs = useRef(new Map());
  const swipeState = useRef({
    id: null,
    dragging: false,
    startX: 0,
    startY: 0,
    baseDx: 0,
    dx: 0,
    direction: null,
    moved: false,
  });

  const SWIPE_REVEAL = 168;   // width of the [Recover][Delete] action strip
  const SWIPE_COMMIT = 260;   // past this, single-swipe = destructive delete

  // ─── Magician escape — triple-tap top strip → /setup ────────────
  const escTaps = useRef({ list: [] });
  useEffect(() => {
    const handler = (e) => {
      const y = e.touches?.[0]?.clientY ?? e.clientY;
      if (typeof y !== "number" || y > 60) return;
      const now = Date.now();
      escTaps.current.list = [...escTaps.current.list, now].filter((t) => now - t < 700);
      if (escTaps.current.list.length >= 3) {
        escTaps.current.list = [];
        navigate("/");
      }
    };
    window.addEventListener("touchstart", handler, { passive: true });
    window.addEventListener("mousedown", handler);
    return () => {
      window.removeEventListener("touchstart", handler);
      window.removeEventListener("mousedown", handler);
    };
  }, [navigate]);

  // ─── Swipe row internals ────────────────────────────────────────
  const setRowTransform = (rowId, dx) => {
    const el = rowRefs.current.get(rowId);
    if (el) el.style.transform = `translate3d(${dx}px,0,0)`;
  };
  const snapRow = (rowId, target) => {
    const el = rowRefs.current.get(rowId);
    if (!el) return;
    el.style.transition = "transform 220ms cubic-bezier(0.22, 1, 0.36, 1)";
    el.style.transform = `translate3d(${target}px,0,0)`;
    // Reset the inline transition after the animation frame so future
    // drags start from a clean slate.
    window.setTimeout(() => {
      if (el) el.style.transition = "";
    }, 240);
  };
  const closeAnySwipedRow = useCallback(() => {
    if (!swipedRowId) return;
    snapRow(swipedRowId, 0);
    setSwipedRowId(null);
  }, [swipedRowId]);
  const handleSwipeStart = (rowId, clientX, clientY) => {
    // If another row is already peeled open, close it first — mimics
    // iOS where only one row can be in the swiped state at a time.
    if (swipedRowId && swipedRowId !== rowId) closeAnySwipedRow();
    const s = swipeState.current;
    s.id = rowId;
    s.dragging = true;
    s.startX = clientX;
    s.startY = clientY;
    s.baseDx = swipedRowId === rowId ? -SWIPE_REVEAL : 0;
    s.dx = s.baseDx;
    s.direction = null;
    s.moved = false;
  };
  const handleSwipeMove = (rowId, clientX, clientY) => {
    const s = swipeState.current;
    if (!s.dragging || s.id !== rowId) return;
    const dx = clientX - s.startX;
    const dy = clientY - s.startY;
    if (!s.direction) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      s.direction = Math.abs(dx) > Math.abs(dy) ? "h" : "v";
      if (s.direction === "v") {
        s.dragging = false;
        return;
      }
    }
    let nextDx = (s.baseDx || 0) + dx;
    if (nextDx > 0) nextDx = 0;
    if (nextDx < -320) nextDx = -320;
    s.dx = nextDx;
    s.moved = true;
    setRowTransform(rowId, nextDx);
  };
  const handleSwipeEnd = (rowId) => {
    const s = swipeState.current;
    if (!s.dragging || s.id !== rowId) return;
    s.dragging = false;
    if (s.dx <= -SWIPE_COMMIT) {
      // Full-strength swipe = destructive delete right away.
      handleDeletePermanently(rowId);
      return;
    }
    if (s.dx <= -SWIPE_REVEAL / 2) {
      snapRow(rowId, -SWIPE_REVEAL);
      setSwipedRowId(rowId);
    } else {
      snapRow(rowId, 0);
      setSwipedRowId((prev) => (prev === rowId ? null : prev));
    }
  };

  // ─── Row actions ────────────────────────────────────────────────
  const handleRecover = (rowId) => {
    const note = popFromTrash(rowId);
    if (!note) return;
    // Insert back at the top of the Notes list. If the id happens to
    // collide with an existing user-created entry (unlikely but
    // possible after a restore-of-something-restored), we let the
    // restored copy win.
    const decoys = loadDecoys();
    const filtered = decoys.filter((n) => n.id !== note.id);
    const restored = { ...note, time: note.time || "" };
    saveDecoys([restored, ...filtered]);
    // Refresh local state + close swipe.
    rowRefs.current.delete(rowId);
    setSwipedRowId(null);
    setTrash(loadTrash());
  };
  const handleDeletePermanently = (rowId) => {
    removeFromTrash(rowId);
    rowRefs.current.delete(rowId);
    setSwipedRowId(null);
    setTrash(loadTrash());
  };
  const handleEmptyAll = () => {
    emptyTrash();
    rowRefs.current.clear();
    setSwipedRowId(null);
    setTrash([]);
    setConfirmOpen(false);
  };

  // ─── Grouping (same buckets iOS uses in the trash) ──────────────
  const grouped = useMemo(() => groupByDeletedAt(trash, langCode), [trash, langCode]);

  return (
    <div
      className="fixed inset-0 w-screen h-screen bg-[#F2F2F7] text-black flex flex-col"
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Helvetica Neue", Arial, sans-serif',
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
      dir={isRTL ? "rtl" : "ltr"}
      onContextMenu={(e) => e.preventDefault()}
      onClick={closeAnySwipedRow}
      data-testid="recently-deleted-page"
    >
      {/* Header (back-link + title + hint) moved INSIDE the scroll
          container as a `sticky top-0` element so items literally
          scroll behind the frosted header — native iOS Notes
          2024/2025 behaviour. */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 pb-24" data-testid="recently-deleted-list">
        {/* ---------- Sticky frosted header ---------- */}
        <div
          className="sticky top-0 z-20 -mx-4 backdrop-blur-xl backdrop-saturate-150 bg-[#F2F2F7]/75"
          data-testid="recently-deleted-sticky-header"
        >
          {/* Top bar */}
          <div className="px-4 pt-2 pb-1 flex items-center justify-between select-none">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate("/folders");
              }}
              className="flex items-center gap-1 text-[#FFB400] text-[17px] leading-none bg-transparent border-0 p-0"
              data-testid="recently-deleted-back"
            >
              <svg width="12" height="20" viewBox="0 0 12 20" fill="none" aria-hidden="true">
                <path d="M10 1L2 10l8 9" stroke="#FFB400" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span className="text-[17px] font-normal">{t("folders", langCode)}</span>
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (trash.length === 0) return;
                setConfirmOpen(true);
              }}
              className={`text-[17px] font-normal bg-transparent border-0 p-0 ${
                trash.length === 0 ? "text-neutral-300 pointer-events-none" : "text-[#FFB400]"
              }`}
              data-testid="recently-deleted-empty"
              aria-disabled={trash.length === 0}
            >
              {t("empty", langCode)}
            </button>
          </div>

          {/* Big page title + hint copy */}
          <div className="px-5 pt-3 pb-3">
            <h1 className="text-[34px] font-bold leading-tight text-black" data-testid="recently-deleted-title">
              {t("recently_deleted", langCode)}
            </h1>
            <p className="text-[13px] text-neutral-500 mt-2 leading-snug" data-testid="recently-deleted-hint">
              {t("trash_hint", langCode)}
            </p>
          </div>
        </div>

        {/* Empty state */}
        {trash.length === 0 && (
          <div className="flex items-center justify-center py-24" data-testid="recently-deleted-empty-state">
            <span className="text-[17px] text-neutral-400">{t("no_deleted_notes", langCode)}</span>
          </div>
        )}

        {/* Grouped list */}
        {trash.length > 0 && (
          <>
            {grouped.map((group) => (
              <section key={group.key} className="mb-4" data-testid={`recently-deleted-group-${group.key}`}>
                <h2 className="text-[13px] font-semibold uppercase tracking-wide text-neutral-500 px-2 mb-2">
                  {group.label}
                </h2>
                <ul className="bg-white rounded-[12px] overflow-hidden divide-y divide-neutral-200/80 shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
                  {group.items.map((n) => (
                    <TrashRow
                      key={n.id}
                      note={n}
                      langCode={langCode}
                      locale={locale}
                      isRTL={isRTL}
                      registerEl={(el) => {
                        if (el) rowRefs.current.set(n.id, el);
                        else rowRefs.current.delete(n.id);
                      }}
                      onPointerDown={handleSwipeStart}
                      onPointerMove={handleSwipeMove}
                      onPointerUp={handleSwipeEnd}
                      onRecover={handleRecover}
                      onDelete={handleDeletePermanently}
                      onCloseSwiped={closeAnySwipedRow}
                      isSwiped={swipedRowId === n.id}
                    />
                  ))}
              </ul>
            </section>
          ))}
          </>
        )}
      </div>

      {/* Empty-all confirmation (iOS-style bottom action sheet) */}
      {confirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          data-testid="recently-deleted-confirm-sheet"
          onClick={(e) => {
            e.stopPropagation();
            setConfirmOpen(false);
          }}
        >
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative w-full max-w-[420px] p-3 space-y-2"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white/95 backdrop-blur rounded-[14px] overflow-hidden">
              <div className="px-4 pt-4 pb-3 text-center">
                <p className="text-[13px] font-semibold text-black leading-snug">
                  {t("empty_confirm_title", langCode)}
                </p>
                <p className="text-[12px] text-neutral-600 mt-1 leading-snug">
                  {t("empty_confirm_body", langCode)}
                </p>
              </div>
              <button
                type="button"
                onClick={handleEmptyAll}
                className="w-full text-center text-[17px] text-red-500 font-normal py-3 border-t border-neutral-200 bg-transparent"
                data-testid="recently-deleted-confirm-delete-all"
              >
                {t("delete_all", langCode)}
              </button>
            </div>
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              className="w-full bg-white/95 backdrop-blur rounded-[14px] text-center text-[17px] text-[#FFB400] font-semibold py-3"
              data-testid="recently-deleted-confirm-cancel"
            >
              {t("cancel", langCode)}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Row component ─────────────────────────────────────────────────
function TrashRow({
  note,
  langCode,
  locale,
  isRTL,
  registerEl,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onRecover,
  onDelete,
  onCloseSwiped,
  isSwiped,
}) {
  const daysLeft = daysLeftInTrash(note.deletedAt);
  const daysLabel =
    daysLeft === 1
      ? t("days_left_one", langCode)
      : (t("days_left_other", langCode) || "").replace("{n}", String(daysLeft));
  const timeLabel = formatTrashTime(note.deletedAt, locale);
  const preview = (note.preview || note.body || "").trim();

  return (
    <li className="relative overflow-hidden bg-[#F2F2F7]">
      {/* Action buttons behind the row — revealed by swiping left. */}
      <div className={`absolute inset-y-0 ${isRTL ? "left-0" : "right-0"} flex`}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRecover(note.id);
          }}
          className="w-[84px] h-full bg-[#5AC8FA] text-white text-[15px] font-semibold flex items-center justify-center"
          data-testid={`trash-row-recover-${note.id}`}
        >
          {t("recover", langCode)}
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(note.id);
          }}
          className="w-[84px] h-full bg-[#FF3B30] text-white text-[15px] font-semibold flex items-center justify-center"
          data-testid={`trash-row-delete-${note.id}`}
        >
          {t("delete_action", langCode)}
        </button>
      </div>

      {/* The row itself (transformed on swipe). */}
      <div
        ref={registerEl}
        className="relative bg-white px-4 py-3 flex flex-col gap-1 will-change-transform"
        style={{ touchAction: "pan-y" }}
        onTouchStart={(e) => {
          const t0 = e.touches[0];
          onPointerDown(note.id, t0.clientX, t0.clientY);
        }}
        onTouchMove={(e) => {
          const t0 = e.touches[0];
          onPointerMove(note.id, t0.clientX, t0.clientY);
        }}
        onTouchEnd={() => onPointerUp(note.id)}
        onTouchCancel={() => onPointerUp(note.id)}
        onMouseDown={(e) => onPointerDown(note.id, e.clientX, e.clientY)}
        onMouseMove={(e) => {
          if (e.buttons === 1) onPointerMove(note.id, e.clientX, e.clientY);
        }}
        onMouseUp={() => onPointerUp(note.id)}
        onMouseLeave={() => onPointerUp(note.id)}
        onClick={(e) => {
          e.stopPropagation();
          // If the row is already peeled open, a plain tap should snap
          // it closed — matches iOS. Otherwise a tap is inert (no note
          // preview in trash) so we just close any other open row.
          if (isSwiped) onCloseSwiped();
        }}
        data-testid={`trash-row-${note.id}`}
      >
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-[17px] font-semibold text-black leading-tight truncate">
            {note.title || t("untitled", langCode)}
          </span>
          <span className="text-[12px] text-neutral-500 shrink-0 tabular-nums">{timeLabel}</span>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-[14px] text-neutral-500 truncate flex-1 leading-snug">
            {preview || " "}
          </span>
          <span
            className="text-[11px] font-medium text-[#FF9500] shrink-0 tabular-nums"
            data-testid={`trash-row-days-${note.id}`}
          >
            {daysLabel}
          </span>
        </div>
      </div>
    </li>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────

// Bucket trash entries into iOS-parity groups: Today / Yesterday /
// Previous 7 Days / Previous 30 Days. Order is preserved: newest
// first inside each bucket.
function groupByDeletedAt(items, langCode) {
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dayMs = 24 * 60 * 60 * 1000;
  const buckets = {
    today: { key: "today", label: t("group_today", langCode), items: [] },
    yesterday: { key: "yesterday", label: t("group_yesterday", langCode), items: [] },
    prev7: { key: "prev7", label: t("group_previous_7_days", langCode), items: [] },
    prev30: { key: "prev30", label: t("group_previous_30_days", langCode), items: [] },
  };
  for (const item of items) {
    const t0 = item.deletedAt;
    if (t0 >= startOfDay) buckets.today.items.push(item);
    else if (t0 >= startOfDay - dayMs) buckets.yesterday.items.push(item);
    else if (t0 >= startOfDay - 7 * dayMs) buckets.prev7.items.push(item);
    else buckets.prev30.items.push(item);
  }
  return Object.values(buckets).filter((b) => b.items.length > 0);
}

// Time label shown on the right of each row. For today: HH:MM. For
// yesterday+ this week: weekday. Older: short date. Matches iOS.
function formatTrashTime(deletedAt, locale) {
  if (typeof deletedAt !== "number") return "";
  const d = new Date(deletedAt);
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dayMs = 24 * 60 * 60 * 1000;
  if (deletedAt >= startOfDay) {
    return d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  }
  if (deletedAt >= startOfDay - 6 * dayMs) {
    return d.toLocaleDateString(locale, { weekday: "short" });
  }
  return d.toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" });
}
